import React, { useMemo } from 'react';
import { StyleSheet, View, useWindowDimensions } from 'react-native';
import DeviceInfo from 'react-native-device-info';
import SkeletonPlaceholder from 'react-native-skeleton-placeholder';
import { useTheme, type Theme } from '../../theme';
import { borderWidth, s, v } from '../../utils/libs';

type ProductCatalogSkeletonProps = {
  /** Number of cards to render */
  count?: number;
  /** Grid column count used by the product FlatList */
  numColumns?: number;
  /** Mirrors Products screen row vs grid layout */
  isRowLayout?: boolean;
  /** Mirrors SettingsSlice showProductImages */
  showProductImages?: boolean;
};

const ProductCatalogSkeleton: React.FC<ProductCatalogSkeletonProps> = ({
  count = 12,
  numColumns = 2,
  isRowLayout = false,
  showProductImages = true,
}) => {
  const { theme } = useTheme();
  const { width } = useWindowDimensions();
  const isTablet = DeviceInfo.isTablet();

  // Mirror ProductItem grid sizing (Product.tsx) so skeleton doesn't feel "off".
  const itemSize = useMemo(() => {
    return isTablet ? (width / 2 - 30) * 0.5 : width / 2 - 30;
  }, [isTablet, width]);

  const cardHeight = useMemo(() => {
    if (isRowLayout) return v(78);
    return showProductImages ? itemSize : itemSize / 1.6;
  }, [isRowLayout, showProductImages, itemSize]);

  const styles = useMemo(
    () =>
      createStyles(
        theme,
        Math.max(1, numColumns),
        cardHeight,
        isRowLayout,
        showProductImages,
        isTablet,
      ),
    [theme, numColumns, cardHeight, isRowLayout, showProductImages, isTablet],
  );

  const items = Array.from({ length: Math.max(1, count) });

  return (
    <SkeletonPlaceholder
      backgroundColor={theme.colors.border}
      highlightColor={theme.colors.surfaceSecondary}>
      <View style={styles.grid}>
        {items.map((_, idx) => (
          <View key={String(idx)} style={styles.cell}>
            <View style={styles.card}>
              {isRowLayout ? (
                <View style={styles.row}>
                  {showProductImages ? <View style={styles.image} /> : null}
                  <View style={styles.rowMain}>
                    <View style={styles.nameBar} />
                    <View style={styles.metaBar} />
                    <View style={styles.priceBar} />
                  </View>
                  <View style={styles.rowControls}>
                    <View style={styles.pill} />
                    <View style={styles.pill} />
                  </View>
                </View>
              ) : (
                <View style={styles.col}>
                  {showProductImages ? <View style={styles.image} /> : null}
                  <View style={styles.nameBar} />
                  <View style={styles.metaBar} />
                  <View style={styles.priceBar} />
                  <View style={styles.controlsRow}>
                    <View style={styles.pill} />
                    <View style={styles.pill} />
                    <View style={styles.pillWide} />
                  </View>
                </View>
              )}
            </View>
          </View>
        ))}
      </View>
    </SkeletonPlaceholder>
  );
};

export default ProductCatalogSkeleton;

const createStyles = (
  theme: Theme,
  numColumns: number,
  cardHeight: number,
  isRowLayout: boolean,
  showProductImages: boolean,
  isTablet: boolean,
) =>
  StyleSheet.create({
    grid: {
      width: '100%',
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
    // Match Products lists: they wrap each card with horizontal padding.
    cell: {
      width: `${100 / Math.max(1, numColumns)}%`,
      paddingHorizontal: s(4),
      paddingVertical: v(4),
    },
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      borderColor: theme.colors.border,
      borderWidth: borderWidth(0.25),
      overflow: 'hidden',
      minHeight: cardHeight,
      padding: s(6),
      justifyContent: 'center',
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
    },
    col: {
      alignItems: 'center',
      gap: v(6),
    },
    image: {
      width: s(isTablet ? 35 : 54),
      height: v(60),
      borderRadius: isTablet ? theme.borderRadius.md : theme.borderRadius.sm,
    },
    rowMain: {
      flex: 1,
      gap: v(6),
    },
    rowControls: {
      alignItems: 'flex-end',
      gap: v(8),
    },
    nameBar: {
      width: showProductImages ? '86%' : '92%',
      height: v(14),
      borderRadius: theme.borderRadius.sm,
      alignSelf: isRowLayout ? 'flex-start' : 'center',
    },
    metaBar: {
      width: showProductImages ? '62%' : '72%',
      height: v(12),
      borderRadius: theme.borderRadius.sm,
      alignSelf: isRowLayout ? 'flex-start' : 'center',
    },
    priceBar: {
      width: '48%',
      height: v(14),
      borderRadius: theme.borderRadius.sm,
      alignSelf: isRowLayout ? 'flex-start' : 'center',
    },
    controlsRow: {
      marginTop: v(2),
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: s(8),
      width: '100%',
    },
    pill: {
      width: s(26),
      height: v(22),
      borderRadius: theme.borderRadius.md,
    },
    pillWide: {
      width: s(44),
      height: v(22),
      borderRadius: theme.borderRadius.md,
    },
  });
